import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import autobind from 'autobind-decorator';
import settings from '../../config/settings';
import ClientAnalytics from '../analytics/ClientAnalytics';
import {getDeviceType} from '../utils/BrowserUtils';
import * as CardUtils from '../utils/CardUtils';
import {cardLoadMode, cardStyles} from '../const/VuCardConst';
import cx from 'classnames';
import VuCardClassic from './VuCardClassic';
import VuCardGeoFiltered from './VuCardGeoFiltered';
import VuCardVuFinder from './VuCardVuFinder';
import VuCardSurvey from './VuCardSurvey';
import VuCardTextReminder from './VuCardTextReminder';
import VuCardLargeVideo from './VuCardLargeVideo';
import VuCardLargeVideoWithCTA from './VuCardLargeVideoWithCTA';
import FrameMessages from '../const/FrameMessages';
import {injectHtml} from '../utils/DomUtils';
import VuCardVuPage from './VuCardVuPage';
import {getCachedPreference, setCachedPreference} from '../fingerprint/FingerprintRedirectPreferenceCache';
import * as RedirectPreferenceUtils from '../utils/RedirectPreferenceUtils';
import {getFingerprints} from '../fingerprint/FingerprintUtils';
import RedirectPreferenceApi from '../api/RedirectPreferenceApi';
import {captureException} from '../utils/SentryUtils';
import {getRedirectCookie, setRedirectCookie} from '../utils/RedirectCookieUtils';
import {getFrameId} from '../ad/utils';
import {renderModes} from '../const/AugmentationConst';

export default class VuCard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isVisible: props.mode !== cardLoadMode.iframe,
      iframeDisplayedOnce: false,
      deviceType: ''
    };

    this.trackingCodeContainer = null;
    this.viewTrackingTimeout = null;
    this.isPageLoadEventSent = false;
  }

  isPopupAd() {
    return this.props.renderMode === renderModes.vupopup;
  }

  componentDidMount() {
    this.applyCardLoadTrackingCodes();

    const {
      mode, isPreloaded, isChangeProviderRequested
    } = this.props;

    const deviceType = getDeviceType();
    this.setState({deviceType});

    ClientAnalytics.initialize();

    if (mode === cardLoadMode.iframe) {
      this.listenForIframeMessages();
      const isVisible = !isPreloaded || this.isPopupAd();
      this.setState({
        isVisible,
        iframeDisplayedOnce: isVisible
      });
    } else {
      if (mode !== cardLoadMode.preview) {
        window.addEventListener('load', this.onPageDisplay);
      }
      if (isChangeProviderRequested) {
        this.applyChangeProviderTrackingCodes(deviceType);
        this.onPageDisplay();
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const hasDisplayed = !prevState.isVisible && this.state.isVisible;
    if (hasDisplayed && this.isPopupAd()) {
      this.setupCloseAd();
      this.setupAutoCloseAd();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('load', this.onPageDisplay);
    window.removeEventListener('message', this.updateIframeVisibility);

    this.closeAdCountdownInterval && clearInterval(this.closeAdCountdownInterval);
    this.closeAdCountdownInterval = false;
  }

  @autobind
  onPageDisplay() {
    if (!this.isPageLoadEventSent && this.props.mode !== cardLoadMode.preview) {
      this.sendCardViewedTrackingEvent();
      this.addVisitedCampaignToFingerprintPreferences();
    }
  }

  async addVisitedCampaignToFingerprintPreferences() {
    const {campaign} = this.props;

    if (campaign && this.isFingerprintRedirectActive()) {
      let fingerprintPreference = null;
      getFingerprints(async ({fingerprint, fingerprint2}) => {
        fingerprintPreference = await this.getFingerprintPreference(fingerprint2, fingerprint);

        if (!fingerprintPreference) {
          fingerprintPreference = RedirectPreferenceUtils.getEmpty();
        }

        if (!RedirectPreferenceUtils.hasVisitedCampaign(fingerprintPreference, campaign.id)) {
          fingerprintPreference = RedirectPreferenceUtils.addVisitedCampaign(fingerprintPreference, campaign.id);
          setCachedPreference(fingerprintPreference);
          try {
            await RedirectPreferenceApi.set({fingerprint, fingerprint2}, fingerprintPreference);
          } catch (e) {
            console.log(e);
          }
        }
      });
    }
  }

  async getFingerprintPreference(fingerprint2, fingerprintOld) {
    try {
      let fingerprintPreference = getCachedPreference();
      if (!fingerprintPreference) {
        fingerprintPreference = await RedirectPreferenceApi.get(fingerprint2, fingerprintOld);
      }
      return fingerprintPreference;
    } catch (e) {
      console.log(e);
    }
  }

  applyCardLoadTrackingCodes() {
    const {trackingCodes} = this.props.record;

    if (!_.isEmpty(trackingCodes)) {
      const loadTrackingCodes = this.getLoadTrackingCodes(trackingCodes);

      loadTrackingCodes.forEach((code) => {
        const wrapper = document.createElement('span');
        this.trackingCodeContainer.appendChild(wrapper);
        injectHtml(wrapper, code);
      });
    }
  }

  applyChangeProviderTrackingCodes(deviceType) {
    const {trackingCodes} = this.props.record;
    const codes = this.getChangeProviderTrackingCodes(trackingCodes, deviceType);

    for (const code of codes) {
      const wrapper = document.createElement('span');
      this.trackingCodeContainer.appendChild(wrapper);
      injectHtml(wrapper, code);
    }
  }

  sendCardViewedTrackingEvent() {
    const {record, mode, rendererId, campaignId, collectionId, showsPageId} = this.props;
    ClientAnalytics.sendCardViewed(
      mode,
      this.getProgramId(record),
      record.title,
      rendererId,
      this.getCardId(record),
      campaignId,
      collectionId,
      showsPageId
    );
    this.isPageLoadEventSent = true;
  }

  listenForIframeMessages() {
    window.addEventListener('message', this.updateIframeVisibility, false);
  }

  @autobind
  updateIframeVisibility(evt) {
    switch (_.get(evt, 'data.title')) {
      case FrameMessages.setVisible: {
        this.setState({isVisible: true, iframeDisplayedOnce: true});
        const time = (_.get(settings, 'widget.vucardImpressionTrackingTimeoutMS') || 2000);
        this.viewTrackingTimeout = setTimeout(() => {
          this.onPageDisplay();
        }, time);
        break;
      }
      case FrameMessages.setHidden: {
        this.handleClose();
        break;
      }
      default:
        break;
    }
  }

  handleClose() {
    this.setState({isVisible: false});
    if (this.viewTrackingTimeout) {
      clearTimeout(this.viewTrackingTimeout);
    }
  }

  getProgramId(record) {
    return record.program ? record.program.id : record.id;
  }

  getCardId(record) {
    return record.program ? record.id : null;
  }

  getFrameId() {
    const {record: {id}, rendererId, campaignId} = this.props;
    const renderer = this.getRenderer(rendererId);
    if (!renderer) {
      return null;
    }
    return getFrameId(renderer.id, id, campaignId);
  }

  render() {
    const {record} = this.props;
    if (!record) {
      return <div className="vuCard"/>;
    }

    return (
      <div className={cx('vuCard', `vuCard--${this.getVuCardStyle()}`)}>
        {
          this.renderClosePopupAd()
        }
        {
          this.renderVuCard()
        }
        {
          this.renderTrackingCodeContainer()
        }
      </div>);
  }

  renderClosePopupAd() {
    if (!this.isPopupAd()) {
      return null;
    }

    return (
      <a className="vupClosePopup"
         onClick={this.handleClosePopup}>
        {'×'}
      </a>
    );
  }

  @autobind
  handleClosePopup() {
    const frameId = this.getFrameId();
    window.parent.postMessage({title: FrameMessages.requestHide, frameId}, '*');
  }

  setupCloseAd() {
    const closeAdTimeout = 0; // this.getClosePopupTimeoutDuration();
    if (!closeAdTimeout) {
      this.setState({
        canCloseAd: true
      });
      return;
    }

    if (closeAdTimeout > 0) {
      this.setState({
        closeAdCountdown: closeAdTimeout,
        canCloseAd: false
      });

      setTimeout(() => this.setState({canCloseAd: true}), closeAdTimeout * 1000);
      this.closeAdCountdownInterval = setInterval(this.updateCloseAdCountdown, 1000);
    }
  }

  @autobind
  updateCloseAdCountdown() {
    let {closeAdCountdown} = this.state;
    closeAdCountdown -= 1;
    this.setState({closeAdCountdown});
    if (closeAdCountdown <= 0) {
      this.closeAdCountdownInterval && clearInterval(this.closeAdCountdownInterval);
    }
  }

  getRenderer() {
    const {campaign, rendererId} = this.props;
    if (!rendererId) {
      return null;
    }
    return _.find(campaign.renderers, {id: rendererId});
  }

  getClosePopupTimeoutDuration() {
    const renderer = this.getRenderer();
    if (renderer) {
      return (renderer.vupopupTimeoutDuration !== 0 && !renderer.vupopupTimeoutDuration) ?
        renderer.vupopupDefaultTimeoutDuration : renderer.vupopupTimeoutDuration;
    }
  }

  getVuCardStyle() {
    const {cardStyle, campaign} = this.props;
    return cardStyle || _.get(campaign, 'vucardStyle') || cardStyles.classic;
  }

  renderVuCard() {
    const {campaign, rendererId, redirectPreference} = this.props;
    const cardProps = {
      ...this.props,
      isVisible: this.state.isVisible,
      rendererId,
      campaign,
      renderer: _.find(_.get(campaign, 'renderers', []), {id: rendererId}),
      redirectPreference,
      setRedirectDestinationPreference: this.setRedirectDestinationPreference
    };

    switch (this.getVuCardStyle()) {
      case cardStyles.classic:
      default:
        return (<VuCardClassic {...cardProps} />);
      case cardStyles.geoFiltered:
        return (<VuCardGeoFiltered {...cardProps} />);
      case cardStyles.vufinder:
        return (<VuCardVuFinder {...cardProps} />);
      case cardStyles.survey:
        return (<VuCardSurvey {...cardProps} />);
      case cardStyles.halfScreenVideo:
        return (<VuCardLargeVideo {...cardProps} />);
      case cardStyles.halfScreenVideoWithCta:
        return (<VuCardLargeVideoWithCTA {...cardProps} />);
      case cardStyles.vuPage:
        return (<VuCardVuPage {...cardProps} />);
      case cardStyles.textReminder:
        return (<VuCardTextReminder {...cardProps} />);
    }
  }

  @autobind
  setRedirectDestinationPreference(destinationName, destinationKind) {
    if (this.isCookieRedirectActive()) {
      this.setRedirectCookie(destinationName, destinationKind);
    }

    if (this.isFingerprintRedirectActive()) {
      this.setFingerprintRedirectPreference(destinationName, destinationKind);
    }
  }

  isCookieRedirectActive() {
    return !_.get(this.props, 'campaign.isCookieRedirectDisabled');
  }

  isFingerprintRedirectActive() {
    return !_.get(this.props, 'campaign.isFingerprintRedirectDisabled');
  }

  setRedirectCookie(destinationName, destinationKind) {
    const {campaignId} = this.props;
    const cookie = getRedirectCookie();
    const updated = RedirectPreferenceUtils.setDestination(cookie, campaignId, destinationName, destinationKind);
    setRedirectCookie(updated);
  }

  async setFingerprintRedirectPreference(destinationName, destinationKind) {
    const {campaignId} = this.props;
    getFingerprints(async ({fingerprint, fingerprint2}) => {
      try {
        const preference = await this.getFingerprintPreference(fingerprint2, fingerprint) || RedirectPreferenceUtils.getEmpty();
        const updated = RedirectPreferenceUtils.setDestination(preference, campaignId, destinationName, destinationKind);
        await RedirectPreferenceApi.set({fingerprint, fingerprint2}, updated);
        setCachedPreference(updated);
      } catch (e) {
        captureException(e);
      }
    });
  }


  getLoadTrackingCodes(trackingCodes) {
    const loadTrackingCodes = [];

    trackingCodes.forEach((codeConfig) => {
      const code = CardUtils.getTrackingCode(codeConfig, this.state.deviceType, 'load');
      if (code) {
        loadTrackingCodes.push(code);
      }
    });
    return loadTrackingCodes;
  }

  getChangeProviderTrackingCodes(trackingCodes, deviceType) {
    const codes = [];

    trackingCodes.forEach((codeConfig) => {
      const code = CardUtils.getTrackingCode(codeConfig, deviceType, 'change_provider');
      if (code) {
        codes.push(code);
      }
    });
    return codes;
  }

  renderTrackingCodeContainer() {
    return (
      <span className="vuCard__trackingCodeContainer"
            style={{position: 'absolute', bottom: '0'}}
            ref={(node) => {
              this.trackingCodeContainer = node;
            }}
      />
    );
  }

  getDisplayDuration() {
    const renderer = this.getRenderer(this.props.rendererId);
    return _.get(renderer, 'displayDuration');
  }

  @autobind
  setupAutoCloseAd() {
    const displayDuration = this.getDisplayDuration();
    if (!displayDuration) {
      return;
    }
    setTimeout(this.handleDisplayDurationPassed, displayDuration * 1000);
  }

  @autobind
  handleDisplayDurationPassed() {
    const frameId = this.getFrameId();
    window.parent.postMessage({title: FrameMessages.requestHide, frameId}, '*');
  }
}

VuCard.propTypes = {
  renderMode: PropTypes.oneOf(Object.values(renderModes)),
  cardStyle: PropTypes.string,
  record: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(Object.values(cardLoadMode)),
  isChangeProviderRequested: PropTypes.bool.isRequired,
  rendererId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showsPageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  redirectPreference: PropTypes.object
};

VuCard.defaultProps = {
  isChangeProviderRequested: false
};
